import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      // Load from localStorage for guest
      const localCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      setCart(localCart);
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity, size, color, product) => {
    if (isAuthenticated) {
      try {
        await axios.post(`${API}/cart/add`, {
          product_id: productId,
          quantity,
          size,
          color
        });
        await fetchCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
      }
    } else {
      // Guest cart in localStorage
      const existingIndex = cart.findIndex(
        item => item.product_id === productId && item.size === size && item.color === color
      );
      
      let newCart;
      if (existingIndex > -1) {
        newCart = [...cart];
        newCart[existingIndex].quantity += quantity;
      } else {
        newCart = [...cart, { product_id: productId, quantity, size, color, product }];
      }
      
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const updateCartItem = async (productId, quantity, size, color) => {
    if (isAuthenticated) {
      try {
        await axios.put(`${API}/cart/update`, {
          product_id: productId,
          quantity,
          size,
          color
        });
        await fetchCart();
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    } else {
      const newCart = cart.map(item => {
        if (item.product_id === productId && item.size === size && item.color === color) {
          return { ...item, quantity };
        }
        return item;
      }).filter(item => item.quantity > 0);
      
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const removeFromCart = async (productId, size, color) => {
    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart/remove/${productId}?size=${size}&color=${color}`);
        await fetchCart();
      } catch (error) {
        console.error('Error removing from cart:', error);
      }
    } else {
      const newCart = cart.filter(
        item => !(item.product_id === productId && item.size === size && item.color === color)
      );
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const clearCart = async () => {
    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart/clear`);
        setCart([]);
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    } else {
      setCart([]);
      localStorage.removeItem('guestCart');
    }
  };

  const cartTotal = cart.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + (price * item.quantity);
  }, 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      cartTotal,
      cartCount,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
